// OptiVelo - AI Marketing Command Center v2.0.1
console.log("OptiVelo Quantum v2.0.1 - User-Friendly Intelligence Active");
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, onSnapshot, setDoc, serverTimestamp, collection } from 'firebase/firestore';
import { Sidebar, ViewState } from './components/Sidebar';
import { Auth } from './components/Auth';
import { SupportChat } from './components/SupportChat';
import { ToastProvider } from './components/Toast';
import { Dashboard } from './components/Dashboard';
import { SettingsView } from './components/SettingsView';
import { Optimizer } from './components/Optimizer';
import { SmartCopywriter } from './components/SmartCopywriter';
import { LandingPagesView } from './components/LandingPagesView';
import { ReportsView } from './components/ReportsView';
import { IntegrationsView } from './components/IntegrationsView';
import { TutorialModal } from './components/TutorialModal';
import { LaunchWizard } from './components/LaunchWizard';
import { MailQuestion, ArrowLeft, AlertTriangle, Menu, X, MessageCircle, Loader2, Check } from 'lucide-react';
import { getDocFromServer, doc as firestoreDoc } from 'firebase/firestore';
import { useLanguage } from './src/lib/LanguageContext';
import { LanguageSelector } from './components/LanguageSelector';

// Lazy loaded components for pages accessed less frequently
const DomainSettings = React.lazy(() => import('./components/DomainSettings').then(m => ({ default: m.DomainSettings })));
const GuideView = React.lazy(() => import('./components/GuideView').then(m => ({ default: m.GuideView })));
const SubscriptionView = React.lazy(() => import('./components/SubscriptionView').then(m => ({ default: m.SubscriptionView })));
const TermsOfService = React.lazy(() => import('./components/TermsOfService').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const PublicPageView = React.lazy(() => import('./components/PublicPageView').then(m => ({ default: m.PublicPageView })));
const NetworkingView = React.lazy(() => import('./components/NetworkingView').then(m => ({ default: m.NetworkingView })));
const ResetPassword = React.lazy(() => import('./components/ResetPassword').then(m => ({ default: m.ResetPassword })));
const AdminSystemStatus = React.lazy(() => import('./components/AdminSystemStatus').then(m => ({ default: m.AdminSystemStatus })));

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      let errorMessage = "Ocorreu um erro inesperado.";
      try {
        const parsedError = JSON.parse(this.state.error.message);
        if (parsedError.error) errorMessage = `Erro no Firestore: ${parsedError.error}`;
      } catch (e) {
        errorMessage = this.state.error?.message || errorMessage;
      }

      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="max-w-md w-full premium-card p-10 text-center space-y-6">
            <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500 mx-auto">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Ops! Algo deu errado</h2>
              <p className="text-slate-400 text-sm">{errorMessage}</p>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-white text-black font-bold rounded-2xl transition-all"
            >
              Recarregar Aplicativo
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// v1.1.0 - Automação OptiVelo Ativa
function App() {
  const { t } = useLanguage();
  const [currentView, setCurrentView] = useState<ViewState>('dashboard');
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isGeminiExpired, setIsGeminiExpired] = useState(false);
  const [isEditingLandingPage, setIsEditingLandingPage] = useState(false);
  const { language } = useLanguage();

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setContactEmail(user.email || '');
    }
    if (userData) {
      setContactName(userData?.displayName || userData?.name || '');
    }
  }, [user, userData]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) {
      setContactError(language === 'en' ? 'Fill in all fields.' : (language === 'es' ? 'Complete todos los campos.' : 'Preencha todos os campos.'));
      return;
    }
    setSubmittingContact(true);
    setContactError(null);
    try {
      await setDoc(doc(collection(db, 'support_messages')), {
        name: contactName,
        email: contactEmail,
        message: contactMessage,
        createdAt: serverTimestamp(),
        source: 'app_dashboard',
        status: 'new'
      });
      setContactSubmitted(true);
      setContactMessage('');
    } catch (err: any) {
      console.error("Error submitting contact form:", err);
      setContactError(language === 'en' ? 'Could not send message. Try again.' : (language === 'es' ? 'No se pudo enviar el mensaje. Intente de nuevo.' : 'Não foi possível enviar sua mensagem. Tente novamente.'));
    } finally {
      setSubmittingContact(false);
    }
  };

  // Reset page editing state when switching main views
  useEffect(() => {
    setIsEditingLandingPage(false);
  }, [currentView]);
  
  // Scroll to top on view change - Enhanced for better reliability
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    };
    scrollToTop();
  }, [currentView]);

  // Check for special routes
  const pathname = window.location.pathname;
  const isPublicPage = pathname.startsWith('/p/');
  const publicPageId = isPublicPage ? pathname.split('/p/')[1] : null;
  const isResetPassword = pathname === '/reset-password';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setUserData(null);
        setLoading(false);
      }
    });

    // Health Check & System Initialization
    const runHealthCheck = async () => {
      console.group("🚀 OptiVelo Quantum Health Check");
      
      // 1. Firebase Connection
      try {
        await getDocFromServer(firestoreDoc(db, 'test', 'connection'));
        console.log("ℹ️ Firestore Client: Ready. Offline-first local cache synchronization enabled.");
      } catch (e: any) {
        if (e.code === 'permission-denied' || e.code === 'not-found') {
          console.log("ℹ️ Firestore Client: Sincronizado.");
        } else {
          console.log("ℹ️ Firestore Client: Operando em modo cache local resiliente.");
        }
      }

      // 2. AI Engine (Quantum Neural Check)
      try {
        const response = await fetch('/api/health');
        if (response.ok) {
          const data = await response.json();
          if (data.isGeminiKeyExpired) {
            setIsGeminiExpired(true);
            console.log("ℹ️ Quantum Neural Engine: Ativo (com fallbacks locais inteligentes).");
          } else {
            console.log("ℹ️ Quantum Neural Engine: Operação Normal.");
          }
        } else {
          console.log("ℹ️ Quantum Neural Engine: Fallback local integrado ativo.");
        }
      } catch (e) {
        console.log("ℹ️ Quantum Neural Engine: Modo cache de performance ativado.");
      }

      // 3. Version Verification
      console.log("📦 System Version: v2.0.1 Quantum");
      console.groupEnd();
    };

    runHealthCheck();

    return () => unsubscribe();
  }, []);

  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [tutorialData, setTutorialData] = useState<any>(null);
  const [isLaunchWizardOpen, setIsLaunchWizardOpen] = useState(false);

  useEffect(() => {
    const handleOpenLaunchWizard = () => setIsLaunchWizardOpen(true);
    window.addEventListener('open-launch-wizard', handleOpenLaunchWizard);
    return () => window.removeEventListener('open-launch-wizard', handleOpenLaunchWizard);
  }, []);

  useEffect(() => {
    const handleViewChange = (e: any) => {
      if (e.detail) {
        setCurrentView(e.detail);
      }
    };
    window.addEventListener('change-view', handleViewChange);
    return () => {
      window.removeEventListener('change-view', handleViewChange);
    };
  }, []);

  useEffect(() => {
    const handleOpenTutorial = (e: any) => {
      if (e.detail) {
        setTutorialData(e.detail);
        setIsTutorialOpen(true);
      }
    };
    window.addEventListener('open-tutorial', handleOpenTutorial);
    return () => {
      window.removeEventListener('open-tutorial', handleOpenTutorial);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        // Auto-sync admin role for owner email
        if (user.email === "geradordeoportunidades@gmail.com" && data.role !== 'admin') {
          await setDoc(doc(db, 'users', user.uid), { role: 'admin' }, { merge: true });
        }
        
        setUserData(data);
      } else {
        // Create user doc if it doesn't exist (e.g. first login)
        const isAdmin = user.email === "geradordeoportunidades@gmail.com";
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 14);
        
        const newData = {
          uid: user.uid,
          email: user.email,
          createdAt: serverTimestamp(),
          role: isAdmin ? 'admin' : 'user',
          subscriptionStatus: isAdmin ? 'enterprise' : 'trial',
          trialUsed: !isAdmin,
          trialEndDate: isAdmin ? null : trialEndDate,
          subscriptionExpiresAt: isAdmin ? null : trialEndDate,
        };
        await setDoc(doc(db, 'users', user.uid), newData);
        setUserData(newData);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!userData) return;
    
    // Check for trial expiration
    const isTrialExpired = userData.subscriptionStatus === 'trial' && userData.trialEndDate && userData.trialEndDate.toDate() < new Date();
    if (isTrialExpired && currentView !== 'subscription' && currentView !== 'settings' && currentView !== 'contact') {
      setCurrentView('subscription');
    }
  }, [userData, currentView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/10 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderContent = () => {
    return (
      <React.Suspense fallback={
        <div className="h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      }>
        {(() => {
          switch (currentView) {
            case 'dashboard':
              return <Dashboard onChangeView={setCurrentView} />;
            case 'optimizer':
              return <Optimizer />;
            case 'smart_copywriter':
              return <SmartCopywriter />;
            case 'landing_pages':
              return <LandingPagesView userData={userData} onEditStateChange={setIsEditingLandingPage} />;
            case 'reports':
              return <ReportsView />;
            case 'integrations':
              return <IntegrationsView />;
            case 'settings':
              return <SettingsView />;
            case 'admin_status':
              return <AdminSystemStatus />;
            case 'quantum_connector':
              return <SettingsView initialTab="connector" />;
            case 'domain_settings':
              return <DomainSettings />;
            case 'guide':
              return <GuideView />;
            case 'subscription':
              return <SubscriptionView />;
            case 'terms':
              return <TermsOfService onBack={() => setCurrentView('dashboard')} />;
            case 'privacy':
              return <PrivacyPolicy onBack={() => setCurrentView('dashboard')} />;
            case 'networking':
              return <NetworkingView />;
            case 'contact':
              return (
                <div className="p-4 sm:p-12 flex items-center justify-center min-h-[60vh]">
                  <div className="max-w-md w-full premium-card p-6 sm:p-10 text-center space-y-6 sm:space-y-8">
                    <div className="w-16 h-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-500 mx-auto">
                      <MailQuestion className="w-8 h-8 animate-pulse" />
                    </div>
                    
                    <div className="space-y-2">
                      <h2 className="text-2xl font-bold text-white">
                        {language === 'en' ? 'Contact Us' : (language === 'es' ? 'Contáctenos' : 'Fale Conosco')}
                      </h2>
                      <p className="text-slate-400 text-sm">
                        {language === 'en' 
                          ? 'Our team is standing by to help you scale your business.' 
                          : (language === 'es' 
                            ? 'Nuestro equipo está listo para ayudarle a expandir su negocio.' 
                            : 'Nossa equipe está pronta para ajudar você a escalar seu negócio.')}
                      </p>
                    </div>

                    {contactSubmitted ? (
                      <div className="space-y-6 py-4 animate-float">
                        <div className="w-12 h-12 bg-emerald-500/15 rounded-full flex items-center justify-center text-emerald-400 mx-auto">
                          <Check className="w-6 h-6" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-bold text-white">
                            {language === 'en' ? 'Message Sent!' : (language === 'es' ? '¡Mensaje Enviado!' : 'Mensagem Enviada!')}
                          </h3>
                          <p className="text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                            {language === 'en' 
                              ? 'Your message has been received. Our team will get back to you shortly!' 
                              : (language === 'es' 
                                ? '¡Recibimos su mensaje! Nuestro equipo se pondrá en contacto pronto.' 
                                : 'Sua mensagem foi recebida. Nossa equipe retornará em breve para o seu e-mail!')}
                          </p>
                        </div>
                        
                        <div className="pt-2">
                          <button 
                            onClick={() => setContactSubmitted(false)}
                            className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white uppercase tracking-widest font-mono rounded-lg transition-all"
                          >
                            {language === 'en' ? 'Send another message' : (language === 'es' ? 'Enviar otro mensaje' : 'Enviar outra mensagem')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleContactSubmit} className="space-y-4 text-left">
                        {contactError && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
                            {contactError}
                          </div>
                        )}

                        <div className="space-y-1.5">
                          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">
                            {language === 'en' ? 'Name' : (language === 'es' ? 'Nombre' : 'Nome')}
                          </label>
                          <input 
                            type="text" 
                            value={contactName} 
                            onChange={(e) => setContactName(e.target.value)}
                            placeholder={language === 'en' ? 'Name' : (language === 'es' ? 'Nombre' : 'Nome')}
                            className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/5 focus:border-indigo-500/50 rounded-lg text-sm text-white placeholder-slate-600 outline-none transition-colors"
                            required
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">
                            {language === 'en' ? 'Email' : (language === 'es' ? 'Correo electrónico' : 'E-mail')}
                          </label>
                          <input 
                            type="email" 
                            value={contactEmail} 
                            onChange={(e) => setContactEmail(e.target.value)}
                            placeholder="exemplo@email.com"
                            className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/5 focus:border-indigo-500/50 rounded-lg text-sm text-white placeholder-slate-600 outline-none transition-colors"
                            required
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-mono uppercase tracking-wider text-slate-400">
                            {language === 'en' ? 'Your Message' : (language === 'es' ? 'Su mensaje' : 'Sua Mensagem')}
                          </label>
                          <textarea 
                            rows={3}
                            value={contactMessage} 
                            onChange={(e) => setContactMessage(e.target.value)}
                            placeholder={language === 'en' ? 'How can we help you scale today?' : (language === 'es' ? '¿Cómo podemos ayudarle?' : 'Como podemos ajudar você a escalar hoje?')}
                            className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/5 focus:border-indigo-500/50 rounded-lg text-sm text-white placeholder-slate-600 outline-none transition-colors resize-none"
                            required
                          />
                        </div>

                        <div className="pt-2">
                          <button 
                            type="submit"
                            disabled={submittingContact}
                            className="w-full py-3.5 bg-indigo-500 hover:bg-indigo-400 text-white font-mono font-bold text-xs uppercase tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            {submittingContact ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>{language === 'en' ? 'Sending...' : 'Enviando...'}</span>
                              </>
                            ) : (
                              <span>{language === 'en' ? 'Send Message' : (language === 'es' ? 'Enviar mensaje' : 'Enviar Mensagem')}</span>
                            )}
                          </button>
                        </div>
                      </form>
                    )}

                    <div className="border-t border-white/5 pt-6 space-y-4">
                      <div className="space-y-2">
                        <p className="text-[12px] text-slate-400 uppercase tracking-widest font-black">
                          {language === 'en' ? 'Or chat with our AI now' : (language === 'es' ? 'O hable con nuestra IA ahora' : 'Ou fale com nossa IA agora')}
                        </p>
                        <button 
                          onClick={() => window.dispatchEvent(new CustomEvent('open-support-chat'))}
                          className="w-full py-3.5 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 font-bold rounded-lg transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-[11px] font-mono"
                        >
                          <MessageCircle className="w-4 h-4 animate-bounce" /> 
                          {language === 'en' ? 'Start Support Chat' : (language === 'es' ? 'Iniciar Chat de Soporte' : 'Iniciar Chat de Suporte')}
                        </button>
                      </div>
                      <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                        {language === 'en' ? 'Responses within 24 business hours' : (language === 'es' ? 'Respuestas en menos de 24h hábiles' : 'Respostas em até 24h úteis')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            default:
              return <Dashboard />;
          }
        })()}
      </React.Suspense>
    );
  };

  return (
    <ErrorBoundary>
      <ToastProvider>
        <React.Suspense fallback={
          <div className="min-h-screen bg-black flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        }>
          {isPublicPage && publicPageId ? (
            <PublicPageView pageId={publicPageId} />
          ) : isResetPassword ? (
            <ResetPassword />
          ) : !user ? (
            <Auth />
          ) : (
            <div className={`flex ${isEditingLandingPage ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-black selection:bg-white/10`}>
              {!isEditingLandingPage && (
                <Sidebar 
                  currentView={currentView} 
                  onChangeView={(view) => {
                    setCurrentView(view);
                    setIsSidebarOpen(false);
                  }} 
                  isOpen={isSidebarOpen}
                  onClose={() => setIsSidebarOpen(false)}
                />
              )}
              
              <main className={`flex-1 ${!isEditingLandingPage ? 'lg:ml-64' : ''} ${isEditingLandingPage ? 'h-screen overflow-hidden' : 'min-h-screen'} relative`}>
                {!isEditingLandingPage && (
                  <header className="sticky top-0 z-20 bg-black/80 backdrop-blur-2xl border-b border-white/5 px-6 lg:px-12 py-6 flex justify-between items-center">
                    <div className="flex items-center gap-4 lg:gap-8">
                      <button 
                        onClick={() => setIsSidebarOpen(true)}
                        className="lg:hidden p-3 -ml-3 text-slate-400 hover:text-white transition-colors"
                      >
                        <Menu className="w-6 h-6" />
                      </button>
                      
                      {currentView !== 'dashboard' && (
                        <button 
                          onClick={() => setCurrentView('dashboard')}
                          className="micro-label flex items-center gap-2 hover:text-white transition-colors"
                        >
                          <ArrowLeft className="w-3 h-3" /> {t('general.back')}
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 lg:gap-12">
                      <LanguageSelector variant="header" />
                      
                      <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full">
                        <div className="w-1 h-1 rounded-full bg-indigo-500 animate-pulse" />
                        <span className="text-[11px] font-black text-indigo-400 uppercase tracking-widest">{t('dashboard.quantumStatus')}</span>
                      </div>
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center justify-end gap-3">
                          {userData?.role === 'admin' && (
                            <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                              {t('sidebar.adminPlan').toUpperCase()}
                            </span>
                          )}
                          <p className="text-sm font-black text-white tracking-tight">{userData?.displayName || user.displayName || user.email?.split('@')[0]}</p>
                        </div>
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest opacity-60">{user.email}</p>
                      </div>
                      <button 
                        onClick={() => auth.signOut()}
                        className="micro-label hover:text-white transition-colors"
                      >
                        {t('general.exit')}
                      </button>
                    </div>
                  </header>
                )}

                {isGeminiExpired && !isEditingLandingPage && (
                  <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 lg:px-12 py-3 flex items-center justify-between text-amber-500 text-xs backdrop-blur-md">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                      <span>
                        <strong>Dica de Configuração:</strong> Sua chave de API do Gemini no Google AI Studio está com cota estourada ou expirada. A plataforma ativou automaticamente o <strong>Modo Inteligente Simulado (Fallbacks locais estruturados de alta conversão)</strong> para manter todas as funcionalidades e testes operando 100% livres de interrupção.
                      </span>
                    </div>
                  </div>
                )}

                <div className={isEditingLandingPage ? "h-full overflow-hidden" : "relative z-10"}>
                  {renderContent()}
                </div>
                <SupportChat />
                <TutorialModal 
                  isOpen={isTutorialOpen} 
                  onClose={() => setIsTutorialOpen(false)} 
                  data={tutorialData} 
                />
                <LaunchWizard 
                  isOpen={isLaunchWizardOpen} 
                  onClose={() => setIsLaunchWizardOpen(false)} 
                  onChangeView={setCurrentView}
                />
              </main>
            </div>
          )}
        </React.Suspense>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
